import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { schedulerService } from '../services/api'
import { Modal, Spinner, EmptyState } from '../components/ui'
import toast from 'react-hot-toast'
import {
  Clock, Plus, Play, Trash2, Edit2, RefreshCw, CheckCircle,
  XCircle, AlertTriangle, Download, FileText, Calendar,
  ChevronDown, Activity, Package, BarChart2, Zap, Info,
  ToggleLeft, ToggleRight,
} from 'lucide-react'

// ─── Constants ───────────────────────────────────────────────────────────────

const TIMEZONES = [
  "Africa/Johannesburg",
  "UTC",
  "Africa/Cairo",
  "Africa/Lagos",
  "Africa/Nairobi",
]

const DOW_OPTIONS = [
  { value: "*",    label: "Every day"       },
  { value: "1-5",  label: "Weekdays (Mon–Fri)" },
  { value: "6,0",  label: "Weekends"        },
  { value: "1",    label: "Monday only"     },
  { value: "5",    label: "Friday only"     },
]

const HOUR_PRESETS = [
  { label: "06:00", h: "6",  m: "0"  },
  { label: "07:00", h: "7",  m: "0"  },
  { label: "08:00", h: "8",  m: "0"  },
  { label: "12:00", h: "12", m: "0"  },
  { label: "16:00", h: "16", m: "0"  },
  { label: "17:00", h: "17", m: "0"  },
  { label: "18:00", h: "18", m: "0"  },
  { label: "20:00", h: "20", m: "0"  },
  { label: "23:00", h: "23", m: "0"  },
  { label: "Custom", h: null, m: null },
]

const JOB_TYPE_ICONS = {
  day_end_report: FileText,
  stock_import:   Package,
}

const STATUS_STYLE = {
  success: { color: "text-green-400", bg: "bg-green-400/10", border: "border-green-400/20", icon: CheckCircle  },
  error:   { color: "text-red-400",   bg: "bg-red-400/10",   border: "border-red-400/20",   icon: XCircle      },
  running: { color: "text-blue-400",  bg: "bg-blue-400/10",  border: "border-blue-400/20",  icon: Activity     },
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtDt = (s) => {
  if (!s) return '—'
  return new Date(s).toLocaleString('en-ZA', {
    day: '2-digit', month: 'short', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

const cronHuman = (job) => {
  const h = job.cron_hour, m = job.cron_minute
  const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  const dow  = job.cron_dow === '*' ? 'every day'
             : job.cron_dow === '1-5' ? 'weekdays'
             : job.cron_dow === '6,0' ? 'weekends'
             : `dow=${job.cron_dow}`
  return `Daily at ${time} (${dow})`
}

// ─── Cron Builder ─────────────────────────────────────────────────────────────

function CronBuilder({ value, onChange }) {
  const [customHour,   setCustomHour]   = useState(value.cron_hour   || "18")
  const [customMinute, setCustomMinute] = useState(value.cron_minute || "0")
  const [useCustom,    setUseCustom]    = useState(
    !HOUR_PRESETS.some(p => p.h === value.cron_hour && p.m === value.cron_minute && p.h !== null)
  )

  const set = (k, v) => onChange({ ...value, [k]: v })

  const selectPreset = (p) => {
    if (p.h === null) {
      setUseCustom(true)
    } else {
      setUseCustom(false)
      onChange({ ...value, cron_hour: p.h, cron_minute: p.m })
    }
  }

  const applyCustom = () => {
    onChange({ ...value, cron_hour: customHour, cron_minute: customMinute })
  }

  const inp = "bg-black/40 border border-[#323232] rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-primary/60 w-16"
  const lbl = "text-xs text-gray-400 mb-1.5 font-medium block"

  return (
    <div className="space-y-4 bg-black/20 border border-[#2a2a2a] rounded-xl p-4">
      <p className={lbl + " !mb-0"}>Schedule Builder</p>

      {/* Time presets */}
      <div>
        <p className={lbl}>Run time</p>
        <div className="flex flex-wrap gap-1.5">
          {HOUR_PRESETS.map(p => {
            const active = p.h === null
              ? useCustom
              : (!useCustom && value.cron_hour === p.h && value.cron_minute === p.m)
            return (
              <button key={p.label} onClick={() => selectPreset(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${active ? 'bg-primary text-white border-primary' : 'bg-surface-card border-surface-border text-gray-400 hover:text-gray-200'}`}>
                {p.label}
              </button>
            )
          })}
        </div>

        {useCustom && (
          <div className="flex items-center gap-2 mt-3">
            <div>
              <p className={lbl}>Hour (0–23)</p>
              <input type="number" min="0" max="23" className={inp}
                value={customHour} onChange={e => setCustomHour(e.target.value)}/>
            </div>
            <div>
              <p className={lbl}>Minute</p>
              <input type="number" min="0" max="59" className={inp}
                value={customMinute} onChange={e => setCustomMinute(e.target.value)}/>
            </div>
            <button onClick={applyCustom} className="btn-primary text-xs mt-4 py-1.5 px-3">Apply</button>
          </div>
        )}
      </div>

      {/* Day-of-week */}
      <div>
        <p className={lbl}>Repeat</p>
        <div className="flex flex-wrap gap-1.5">
          {DOW_OPTIONS.map(opt => (
            <button key={opt.value} onClick={() => set('cron_dow', opt.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${value.cron_dow === opt.value ? 'bg-primary text-white border-primary' : 'bg-surface-card border-surface-border text-gray-400 hover:text-gray-200'}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Timezone */}
      <div>
        <p className={lbl}>Timezone</p>
        <select
          value={value.timezone || 'Africa/Johannesburg'}
          onChange={e => set('timezone', e.target.value)}
          className="bg-black/40 border border-[#323232] rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-primary/60"
        >
          {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
        </select>
      </div>

      {/* Preview */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg px-3 py-2">
        <p className="text-xs text-gray-400">Preview</p>
        <p className="text-sm text-primary font-medium mt-0.5">
          Runs {DOW_OPTIONS.find(d => d.value === value.cron_dow)?.label?.toLowerCase() || value.cron_dow} at{' '}
          {String(value.cron_hour).padStart(2,'0')}:{String(value.cron_minute).padStart(2,'0')}{' '}
          ({value.timezone || 'Africa/Johannesburg'})
        </p>
        <p className="text-gray-600 text-xs mt-0.5 font-mono">
          cron: {value.cron_minute} {value.cron_hour} {value.cron_dom} {value.cron_month} {value.cron_dow}
        </p>
      </div>
    </div>
  )
}

// ─── Job config panels ────────────────────────────────────────────────────────

function DayEndConfig({ config, onChange }) {
  const set = (k, v) => onChange({ ...config, [k]: v })
  const inp = "w-full bg-black/40 border border-[#323232] rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-primary/60 placeholder:text-gray-600"
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-gray-400 mb-1.5 font-medium block">Email recipients <span className="text-gray-600">(comma separated, optional)</span></label>
        <input className={inp} placeholder="manager@tonysliquor.co.za, owner@tonysliquor.co.za"
          value={config.email_recipients || ''} onChange={e => set('email_recipients', e.target.value)}/>
      </div>
      <label className="flex items-center gap-3 cursor-pointer">
        <div onClick={() => set('report_yesterday', !config.report_yesterday)}
          className={`w-10 h-5 rounded-full relative transition-colors ${config.report_yesterday ? 'bg-primary' : 'bg-gray-600'}`}>
          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${config.report_yesterday ? 'translate-x-5' : 'translate-x-0.5'}`}/>
        </div>
        <span className="text-sm text-gray-300">Always report on yesterday's date</span>
      </label>
      <div className="bg-black/20 border border-[#2a2a2a] rounded-lg p-3 text-xs text-gray-500 flex gap-2">
        <Info size={12} className="flex-shrink-0 mt-0.5 text-gray-600"/>
        <span>Reports are saved as CSV to the output directory and listed in the Files tab below. Contains orders, line items (POS format), and stock snapshot.</span>
      </div>
    </div>
  )
}

function StockImportConfig({ config, onChange }) {
  const set = (k, v) => onChange({ ...config, [k]: v })
  const inp = "w-full bg-black/40 border border-[#323232] rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-primary/60 placeholder:text-gray-600"
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-gray-400 mb-1.5 font-medium block">Stock inbox directory</label>
        <input className={inp} placeholder="/tmp/tonys_stock_inbox"
          value={config.watch_dir || ''} onChange={e => set('watch_dir', e.target.value)}/>
      </div>
      <div className="bg-black/20 border border-[#2a2a2a] rounded-lg p-3 text-xs text-gray-500 flex gap-2">
        <Info size={12} className="flex-shrink-0 mt-0.5 text-gray-600"/>
        <span>Drop a CSV file into this folder before the job runs. Required columns: <code className="text-gray-400">StockCode</code> and <code className="text-gray-400">Level</code> (or Qty/Quantity/OnHand). Processed files are moved to <code className="text-gray-400">/done</code>.</span>
      </div>
    </div>
  )
}

const CONFIG_PANELS = {
  day_end_report: DayEndConfig,
  stock_import:   StockImportConfig,
}

// ─── Job Form Modal ───────────────────────────────────────────────────────────

const EMPTY_CRON = { cron_minute: "0", cron_hour: "18", cron_dom: "*", cron_month: "*", cron_dow: "*", timezone: "Africa/Johannesburg" }

function JobFormModal({ initial, jobTypes, onClose, onSave }) {
  const isEdit = !!initial
  const defaultType = jobTypes[0]?.type || "day_end_report"
  const defaultMeta = jobTypes.find(t => t.type === (initial?.job_type || defaultType))

  const [jobType, setJobType] = useState(initial?.job_type || defaultType)
  const [name,    setName]    = useState(initial?.name || defaultMeta?.label || "")
  const [enabled, setEnabled] = useState(initial?.enabled ?? true)
  const [cron,    setCron]    = useState({
    cron_minute: initial?.cron_minute || "0",
    cron_hour:   initial?.cron_hour   || "18",
    cron_dom:    initial?.cron_dom    || "*",
    cron_month:  initial?.cron_month  || "*",
    cron_dow:    initial?.cron_dow    || "*",
    timezone:    initial?.timezone    || "Africa/Johannesburg",
  })
  const [config, setConfig] = useState(
    initial?.config || jobTypes.find(t => t.type === jobType)?.default_config || {}
  )
  const [saving, setSaving] = useState(false)

  const handleTypeChange = (t) => {
    setJobType(t)
    const meta = jobTypes.find(j => j.type === t)
    if (!isEdit) {
      setName(meta?.label || t)
      if (meta?.default_config) setConfig(meta.default_config)
      if (meta?.default_cron) {
        setCron(prev => ({
          ...prev,
          cron_hour: meta.default_cron.hour,
          cron_minute: meta.default_cron.minute,
          cron_dow: meta.default_cron.dow,
        }))
      }
    }
  }

  const handleSave = async () => {
    if (!name.trim()) return toast.error('Name is required')
    setSaving(true)
    try {
      await onSave({ job_type: jobType, name, enabled, ...cron, config })
      onClose()
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const ConfigPanel = CONFIG_PANELS[jobType]
  const inp = "w-full bg-black/40 border border-[#323232] rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-primary/60 placeholder:text-gray-600"

  return (
    <Modal title={isEdit ? `Edit: ${initial.name}` : "New Scheduled Job"} onClose={onClose} size="lg">
      <div className="space-y-5 max-h-[80vh] overflow-y-auto pr-1">

        {/* Job type selector */}
        {!isEdit && (
          <div>
            <label className="text-xs text-gray-400 mb-2 font-medium block">Job Type</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {jobTypes.map(jt => {
                const Icon = JOB_TYPE_ICONS[jt.type] || Zap
                const active = jobType === jt.type
                return (
                  <button key={jt.type} onClick={() => handleTypeChange(jt.type)}
                    className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-colors ${active ? 'bg-primary/10 border-primary/40' : 'bg-black/30 border-[#323232] hover:border-gray-500'}`}>
                    <Icon size={18} className={active ? 'text-primary mt-0.5' : 'text-gray-500 mt-0.5'}/>
                    <div>
                      <p className={`text-sm font-semibold ${active ? 'text-primary' : 'text-gray-300'}`}>{jt.label}</p>
                      <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{jt.description}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Name */}
        <div>
          <label className="text-xs text-gray-400 mb-1.5 font-medium block">Job Name</label>
          <input className={inp} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Daily Close Report"/>
        </div>

        {/* Cron builder */}
        <CronBuilder value={cron} onChange={setCron}/>

        {/* Job-specific config */}
        {ConfigPanel && (
          <div>
            <label className="text-xs text-gray-400 mb-2 font-medium block">Job Settings</label>
            <ConfigPanel config={config} onChange={setConfig}/>
          </div>
        )}

        {/* Active toggle */}
        <div className="bg-black/30 border border-[#323232] rounded-xl p-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <div onClick={() => setEnabled(!enabled)}
              className={`w-10 h-5 rounded-full relative transition-colors ${enabled ? 'bg-primary' : 'bg-gray-600'}`}>
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`}/>
            </div>
            <span className="text-sm text-gray-300">Enabled (will run on schedule)</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-1 border-t border-surface-border">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-1.5">
            {saving ? <Spinner size={14}/> : <CheckCircle size={14}/>}
            {isEdit ? 'Save Changes' : 'Create Job'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Run log modal ────────────────────────────────────────────────────────────

function RunLogModal({ job, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ['scheduler', 'runs', job.id],
    queryFn: () => schedulerService.getRuns(job.id),
    staleTime: 10 * 1000,
    refetchInterval: 10 * 1000,
  })
  const runs = Array.isArray(data) ? data : (data?.data || [])

  return (
    <Modal title={`Run History — ${job.name}`} onClose={onClose} size="lg">
      {isLoading ? <div className="flex justify-center py-8"><Spinner/></div> : (
        runs.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-8">No runs yet — schedule it or trigger manually.</p>
        ) : (
          <div className="space-y-2 max-h-[70vh] overflow-y-auto">
            {runs.map(r => {
              const st = STATUS_STYLE[r.status] || STATUS_STYLE.error
              const Icon = st.icon
              return (
                <div key={r.id} className={`rounded-xl border ${st.border} ${st.bg} p-3`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <Icon size={14} className={st.color}/>
                      <span className={`text-xs font-semibold ${st.color} capitalize`}>{r.status}</span>
                      <span className="text-gray-600 text-xs">·</span>
                      <span className="text-gray-400 text-xs">{r.triggered_by === 'manual' ? '🖱 Manual' : '⏰ Scheduled'}</span>
                    </div>
                    <span className="text-gray-500 text-xs">{fmtDt(r.started_at)}</span>
                  </div>
                  {r.output && <p className="text-gray-300 text-xs leading-relaxed">{r.output}</p>}
                  {r.output_file && (
                    <a
                      href={`/api${schedulerService.downloadUrl(r.output_file.split('/').pop())}`}
                      className="inline-flex items-center gap-1.5 mt-2 text-xs text-primary hover:text-primary/80"
                      download
                    >
                      <Download size={11}/> {r.output_file.split('/').pop()}
                    </a>
                  )}
                </div>
              )
            })}
          </div>
        )
      )}
    </Modal>
  )
}

// ─── Job Card ─────────────────────────────────────────────────────────────────

function JobCard({ job, onEdit, onDelete, onRun, onViewRuns }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const Icon = JOB_TYPE_ICONS[job.job_type] || Zap
  const st   = job.last_run_status ? (STATUS_STYLE[job.last_run_status] || STATUS_STYLE.error) : null
  const LastIcon = st?.icon

  return (
    <div className={`bg-surface-card border rounded-2xl overflow-hidden transition-colors ${job.enabled ? 'border-surface-border hover:border-gray-500' : 'border-[#2a2a2a] opacity-60'}`}>
      {/* Status stripe */}
      <div className={`h-1 w-full ${job.enabled ? 'bg-primary' : 'bg-gray-700'}`}/>

      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${job.enabled ? 'bg-primary/10' : 'bg-white/5'}`}>
              <Icon size={16} className={job.enabled ? 'text-primary' : 'text-gray-500'}/>
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm leading-tight">{job.name}</h3>
              <p className="text-gray-500 text-xs mt-0.5">{job.job_type_label}</p>
            </div>
          </div>
          <div className="relative flex-shrink-0">
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-gray-300">
              <ChevronDown size={14}/>
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 bg-surface-card border border-surface-border rounded-xl shadow-xl z-20 w-36 overflow-hidden">
                {[
                  { label: 'Edit', icon: Edit2, fn: onEdit },
                  { label: 'Run Now', icon: Play, fn: onRun },
                  { label: 'View Runs', icon: Activity, fn: onViewRuns },
                  { label: 'Delete', icon: Trash2, fn: onDelete, danger: true },
                ].map(({ label, icon: I, fn, danger }) => (
                  <button key={label} onClick={() => { fn(); setMenuOpen(false) }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/5 ${danger ? 'text-red-400' : 'text-gray-300'}`}>
                    <I size={12}/> {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Schedule */}
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Clock size={11} className="text-gray-600"/>
          <span>{cronHuman(job)}</span>
        </div>

        {/* Next run */}
        {job.next_run && (
          <div className="flex items-center gap-1.5 text-xs text-primary/70">
            <Calendar size={11}/>
            <span>Next: {fmtDt(job.next_run)}</span>
          </div>
        )}

        {/* Last run */}
        {job.last_run_at && st && (
          <div className={`flex items-center gap-1.5 text-xs ${st.color}`}>
            {LastIcon && <LastIcon size={11}/>}
            <span>Last: {fmtDt(job.last_run_at)} — {job.last_run_status}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1 border-t border-surface-border">
          <button onClick={onRun} className="flex-1 btn-primary text-xs py-1.5 flex items-center justify-center gap-1.5">
            <Play size={11}/> Run Now
          </button>
          <button onClick={onViewRuns} className="px-3 py-1.5 rounded-xl border border-surface-border text-gray-400 hover:text-gray-200 text-xs flex items-center gap-1">
            <Activity size={11}/> History
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Files Tab ────────────────────────────────────────────────────────────────

function FilesTab() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['scheduler', 'outputs'],
    queryFn: schedulerService.getOutputs,
    staleTime: 30 * 1000,
  })
  const files = Array.isArray(data) ? data : (data?.data || [])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">Generated report files available for download.</p>
        <button onClick={() => refetch()} className="btn-secondary text-xs flex items-center gap-1.5"><RefreshCw size={12}/> Refresh</button>
      </div>
      {isLoading ? <div className="flex justify-center py-8"><Spinner/></div> :
        files.length === 0 ? (
          <div className="bg-surface-card border border-surface-border rounded-xl flex flex-col items-center py-12 gap-2">
            <FileText size={32} className="text-gray-600"/>
            <p className="text-gray-500 text-sm">No files yet — run a Day-End Report job to generate one.</p>
          </div>
        ) : (
          <div className="bg-surface-card border border-surface-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="admin-thead border-b border-[#2a2a2a]">
                  {['Filename', 'Size', 'Generated', 'Download'].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-gray-400 text-xs font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {files.map((f, i) => (
                  <tr key={f.filename} className={`border-b border-surface-border/40 last:border-0 ${i%2===0?'':'bg-white/[0.02]'}`}>
                    <td className="px-4 py-2.5 text-white font-medium text-xs font-mono">{f.filename}</td>
                    <td className="px-4 py-2.5 text-gray-400 text-xs">{f.size_kb} KB</td>
                    <td className="px-4 py-2.5 text-gray-400 text-xs">{fmtDt(f.modified)}</td>
                    <td className="px-4 py-2.5">
                      <a href={`/api${schedulerService.downloadUrl(f.filename)}`}
                        className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium" download>
                        <Download size={12}/> Download
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'jobs',  label: 'Scheduled Jobs', icon: Clock    },
  { key: 'files', label: 'Generated Files', icon: FileText },
]

export default function Scheduler() {
  const [tab, setTab]         = useState('jobs')
  const [showForm, setShowForm] = useState(false)
  const [editJob, setEditJob]   = useState(null)
  const [viewRunsJob, setViewRunsJob] = useState(null)
  const qc = useQueryClient()

  const { data: jobsData, isLoading } = useQuery({
    queryKey: ['scheduler', 'jobs'],
    queryFn: schedulerService.getJobs,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  })

  const { data: typesData } = useQuery({
    queryKey: ['scheduler', 'job-types'],
    queryFn: schedulerService.getJobTypes,
    staleTime: 60 * 60 * 1000,
  })

  const { data: statusData } = useQuery({
    queryKey: ['scheduler', 'status'],
    queryFn: schedulerService.getStatus,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  })

  const jobs     = Array.isArray(jobsData) ? jobsData : (jobsData?.data || [])
  const jobTypes = Array.isArray(typesData) ? typesData : (typesData?.data || [])

  const createMutation = useMutation({
    mutationFn: schedulerService.createJob,
    onSuccess: () => { qc.invalidateQueries(['scheduler']); toast.success('Job created!') },
    onError:   () => toast.error('Failed to create job'),
  })
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => schedulerService.updateJob(id, data),
    onSuccess: () => { qc.invalidateQueries(['scheduler']); toast.success('Job updated') },
    onError:   () => toast.error('Failed to update job'),
  })
  const deleteMutation = useMutation({
    mutationFn: schedulerService.deleteJob,
    onSuccess: () => { qc.invalidateQueries(['scheduler']); toast.success('Job deleted') },
    onError:   () => toast.error('Failed to delete job'),
  })
  const runMutation = useMutation({
    mutationFn: schedulerService.runNow,
    onSuccess: (_, id) => {
      toast.success('Job triggered — check History in a moment')
      setTimeout(() => qc.invalidateQueries(['scheduler']), 3000)
    },
    onError: () => toast.error('Failed to trigger job'),
  })

  const handleSave = async (form) => {
    if (editJob) {
      await updateMutation.mutateAsync({ id: editJob.id, data: form })
    } else {
      await createMutation.mutateAsync(form)
    }
  }

  const schedulerRunning = statusData?.running ?? statusData?.data?.running ?? false
  const activeJobs = jobs.filter(j => j.enabled).length

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Clock size={22} className="text-primary"/> Scheduler
          </h1>
          <p className="text-gray-400 text-sm mt-0.5">Automate reports, stock imports, and recurring tasks</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Engine status pill */}
          <div className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border ${schedulerRunning ? 'text-green-400 bg-green-400/10 border-green-400/20' : 'text-red-400 bg-red-400/10 border-red-400/20'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${schedulerRunning ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`}/>
            {schedulerRunning ? 'Scheduler Running' : 'Scheduler Offline'}
          </div>
          <button onClick={() => qc.invalidateQueries(['scheduler'])} className="btn-secondary flex items-center gap-1.5 text-xs">
            <RefreshCw size={13}/> Refresh
          </button>
          <button onClick={() => { setEditJob(null); setShowForm(true) }} className="btn-primary flex items-center gap-1.5">
            <Plus size={15}/> New Job
          </button>
        </div>
      </div>

      {/* KPI bar */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Active Jobs',    value: activeJobs,         icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-400/10' },
          { label: 'Total Jobs',     value: jobs.length,        icon: Clock,       color: 'text-primary',   bg: 'bg-primary/10'   },
          { label: 'Job Types',      value: jobTypes.length,    icon: Zap,         color: 'text-gold',      bg: 'bg-gold/10'      },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-surface-card border border-surface-border rounded-xl p-3 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
              <Icon size={16} className={color}/>
            </div>
            <div>
              <p className="text-white font-bold text-lg leading-none">{value}</p>
              <p className="text-gray-500 text-xs mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-black/30 border border-surface-border rounded-xl p-1 w-fit">
        {TABS.map(t => { const I = t.icon; return (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab===t.key?'bg-primary text-white':'text-gray-400 hover:text-gray-200'}`}>
            <I size={14}/> {t.label}
          </button>
        )})}
      </div>

      {/* Tab content */}
      {tab === 'jobs' && (
        isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg"/></div>
        ) : jobs.length === 0 ? (
          <EmptyState icon={Clock} title="No scheduled jobs yet" message="Create your first job to automate day-end reports and stock imports"/>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {jobs.map(job => (
              <JobCard
                key={job.id}
                job={job}
                onEdit={() => { setEditJob(job); setShowForm(true) }}
                onDelete={() => { if (confirm(`Delete "${job.name}"?`)) deleteMutation.mutate(job.id) }}
                onRun={() => runMutation.mutate(job.id)}
                onViewRuns={() => setViewRunsJob(job)}
              />
            ))}
          </div>
        )
      )}
      {tab === 'files' && <FilesTab/>}

      {/* Modals */}
      {showForm && (
        <JobFormModal
          initial={editJob}
          jobTypes={jobTypes}
          onClose={() => { setShowForm(false); setEditJob(null) }}
          onSave={handleSave}
        />
      )}
      {viewRunsJob && <RunLogModal job={viewRunsJob} onClose={() => setViewRunsJob(null)}/>}
    </div>
  )
}
